'use client';

import {
	Autocomplete,
	Button,
	Grid,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { InverseButton } from '@parkchirp/ui-library';
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';

import { BillingProfile, FeesForRates, ProductCode, RateCode, RateV2, TaxTableTax } from '../../../types';
import { dayOfTheMonthFieldOptions } from '../../../utils';
import { BillingProfileAutoComplete } from './billingProfileAutoComplete';
import { FeeAutoComplete } from './feeAutoComplete';
import { ProductRateCodeAutoComplete } from './productRateCodeAutoComplete';
import { TaxTableTaxesAutoComplete } from './taxTableTaxesAutoComplete';

interface Props {
	FMPDay: number;
	setFMPDay: Dispatch<SetStateAction<number>>;
	firstMonthProration: string;
	setFirstMonthProration: Dispatch<SetStateAction<string>>;
	setTabValue: Dispatch<SetStateAction<string>>;
	missingFields: string;
	setMissingFields: Dispatch<SetStateAction<string>>;
	billingProfileOptions: any[];
	billingProfile: BillingProfile;
	setBillingProfile: Dispatch<SetStateAction<BillingProfile>>;
	selectedRate: RateV2 | null;
	setActiveStep: Dispatch<SetStateAction<number>>;
	feeOptions: FeesForRates[];
	setMonthlyFee: Dispatch<SetStateAction<any>>;
	monthlyFee: any;
	isChild: boolean;
	childDiscountType: string;
	theme: any;
	isDefault: boolean;
	defaultRateTax1: TaxTableTax | null;
	defaultRateTax2: TaxTableTax | null;
	defaultRateTax3: TaxTableTax | null;
	defaultRateTax4: TaxTableTax | null;
	setDefaultRateTax1: Dispatch<SetStateAction<TaxTableTax>>;
	setDefaultRateTax2: Dispatch<SetStateAction<TaxTableTax>>;
	setDefaultRateTax3: Dispatch<SetStateAction<TaxTableTax>>;
	setDefaultRateTax4: Dispatch<SetStateAction<TaxTableTax>>;
	taxTableOptions: TaxTableTax[];
	fixedFeeName: string;
	setFixedFeeName: Dispatch<SetStateAction<string>>;
	fixedFeeNetAmount: number | null;
	setFixedFeeNetAmount: Dispatch<SetStateAction<number | null>>;
	rateCodes: Partial<RateCode>[];
	productCodes: Partial<ProductCode>[];
	defaultRateCode: Partial<RateCode>;
	setDefaultRateCode: Dispatch<SetStateAction<Partial<RateCode>>>;
	defaultProductCode: Partial<ProductCode>;
	setDefaultProductCode: Dispatch<SetStateAction<Partial<ProductCode>>>;
}

const FixedFeeFormToggle = ({ showFixedFeeForm, setShowFixedFeeForm }) => (
	<Grid item xs={3}>
		<Button
			onClick={() => {
				setShowFixedFeeForm(!showFixedFeeForm);
			}}
			variant='outlined'
			fullWidth
		>
			{showFixedFeeForm ? 'Use Existing Monthly Fee' : 'Create New Fixed Fee'}
		</Button>
	</Grid>
);

const FixedFeeForm = ({ fixedFeeName, setFixedFeeName, fixedFeeNetAmount, setFixedFeeNetAmount }) => (
	<>
		<Grid item xs={6}>
			<InputLabel>
				Fee Name
				<span className='pca__ratesForm-required'> *</span>
			</InputLabel>
			<TextField
				fullWidth
				placeholder='Fee Name'
				value={fixedFeeName}
				onChange={(e) => {
					setFixedFeeName(e.target.value);
				}}
			/>
		</Grid>
		<Grid item xs={6}>
			<InputLabel>
				Fee Net Amount
				<span className='pca__ratesForm-required'> *</span>
			</InputLabel>
			<TextField
				fullWidth
				type='number'
				placeholder='Fee Net Amount'
				value={fixedFeeNetAmount}
				onChange={(e) => {
					setFixedFeeNetAmount(e.target.value);
				}}
				InputProps={{
					startAdornment: <InputAdornment position='start'>$</InputAdornment>,
					sx: { paddingLeft: '5px' },
				}}
				inputProps={{
					step: 0.01,
					min: 0,
					pattern: '[0-9]*',
					inputMode: 'numeric',
					'data-cy': 'fee-input-input',
				}}
			/>
		</Grid>
	</>
);

const BillingAndRates: FC<Props> = ({
	FMPDay,
	setFMPDay,
	firstMonthProration,
	setFirstMonthProration,
	setTabValue,
	missingFields,
	setMissingFields,
	billingProfileOptions,
	billingProfile,
	setBillingProfile,
	selectedRate,
	setActiveStep,
	feeOptions,
	monthlyFee,
	setMonthlyFee,
	isChild,
	childDiscountType,
	theme,
	isDefault,
	defaultRateTax1,
	defaultRateTax2,
	defaultRateTax3,
	defaultRateTax4,
	taxTableOptions,
	setDefaultRateTax1,
	setDefaultRateTax2,
	setDefaultRateTax3,
	setDefaultRateTax4,
	fixedFeeName,
	setFixedFeeName,
	fixedFeeNetAmount,
	setFixedFeeNetAmount,
	rateCodes,
	productCodes,
	defaultRateCode,
	setDefaultRateCode,
	defaultProductCode,
	setDefaultProductCode,
}) => {
	// Fixed Fee Form State
	const [showFixedFeeForm, setShowFixedFeeForm] = useState(false);

	const resetFixedFeeForm = () => {
		setFixedFeeName('');
		setFixedFeeNetAmount(null);
	};

	useEffect(() => {
		if (!showFixedFeeForm) {
			resetFixedFeeForm();
		}
	}, [showFixedFeeForm]);

	const handleNext = () => {
		const requiredFields = { billingProfile, firstMonthProration, FMPDay };
		if (firstMonthProration !== 'HALF_MONTH') {
			delete requiredFields.FMPDay;
		}
		let errorMessage = '';
		Object.entries(requiredFields).forEach((e) => {
			const key = e[0];
			const value = e[1];
			if (!value && value !== 0) {
				const newValue = `${key}, `;
				errorMessage += newValue;
			}
		});
		if (!monthlyFee && !isDefault) {
			errorMessage += 'monthlyFeeName';
		}
		if (errorMessage) {
			setMissingFields(errorMessage);
		} else {
			setMissingFields('');
			setActiveStep(isChild ? 4 : 3);
			setTabValue(isChild ? '5' : '4');
		}
	};
	const days = Object.entries(dayOfTheMonthFieldOptions);
	return (
		<Grid spacing={2} container>
			<Grid item xs={12}>
				<InputLabel>
					Billing Profile<span className='pca__ratesForm-required'>*</span>
				</InputLabel>
				<BillingProfileAutoComplete
					id='billing-profile-select-menu'
					error={missingFields.includes('billingProfile')}
					disabled={!!selectedRate}
					options={billingProfileOptions}
					selectedOption={billingProfile}
					handleChange={setBillingProfile}
				/>
			</Grid>
			<Grid item xs={firstMonthProration && firstMonthProration === 'HALF_MONTH' ? 8 : 12}>
				<InputLabel>
					First Month Proration <span className='pca__ratesForm-required'>*</span>
				</InputLabel>
				<Select
					fullWidth
					value={firstMonthProration}
					required
					disabled={isChild}
					error={missingFields.includes('firstMonthProration')}
					onChange={(e) => setFirstMonthProration(e.target.value)}
					data-cy='first-month-proration-select-menu'
				>
					<MenuItem value='DAILY'>Daily</MenuItem>
					<MenuItem value='HALF_MONTH'>Half Month</MenuItem>
					<MenuItem value='FULL_MONTH'>Full Month</MenuItem>
				</Select>
			</Grid>
			{firstMonthProration && firstMonthProration === 'HALF_MONTH' && (
				<Grid item xs={4}>
					<div className='pca__ratesForm-flexRow' style={{ marginTop: '23px' }}>
						<InputLabel>on or after the</InputLabel>
						<Select
							style={{ margin: '0px 10px' }}
							value={FMPDay}
							required
							disabled={isChild}
							onChange={(e) => setFMPDay(Number(e.target.value))}
							error={missingFields.includes('day')}
							data-cy='proration-date-select-menu'
						>
							{days.map((day) => (
								<MenuItem key={day[0]} value={day[0]}>
									{day[1]}
								</MenuItem>
							))}
						</Select>
						<InputLabel>of the month </InputLabel>
					</div>
				</Grid>
			)}
			{/* only disabled for parent rate when editing */}
			{/* disabled for children rate when editing an already submitted child */}
			{/* disabled for children rate not submitting when discount type !== 'FEE' */}
			{isDefault ? (
				<>
					<Grid item xs={3}>
						<InputLabel>Tax 1</InputLabel>
						<TaxTableTaxesAutoComplete
							id='monthly-tax-table-tax-1-select-menu'
							handleChange={setDefaultRateTax1}
							selectedTax={defaultRateTax1}
							taxTableTaxes={taxTableOptions}
							taxNumber={1}
						/>
					</Grid>
					<Grid item xs={3}>
						<InputLabel>Tax 2</InputLabel>
						<TaxTableTaxesAutoComplete
							id='monthly-tax-table-tax-1-select-menu'
							handleChange={setDefaultRateTax2}
							selectedTax={defaultRateTax2}
							taxTableTaxes={taxTableOptions}
							taxNumber={2}
						/>
					</Grid>
					<Grid item xs={3}>
						<InputLabel>Tax 3</InputLabel>
						<TaxTableTaxesAutoComplete
							id='monthly-tax-table-tax-1-select-menu'
							handleChange={setDefaultRateTax3}
							selectedTax={defaultRateTax3}
							taxTableTaxes={taxTableOptions}
							taxNumber={3}
						/>
					</Grid>
					<Grid item xs={3}>
						<InputLabel>Tax 4</InputLabel>
						<TaxTableTaxesAutoComplete
							id='monthly-tax-table-tax-1-select-menu'
							handleChange={setDefaultRateTax4}
							selectedTax={defaultRateTax4}
							taxTableTaxes={taxTableOptions}
							taxNumber={4}
						/>
					</Grid>
					<Grid item xs={6}>
						<InputLabel>Product Code</InputLabel>
						{productCodes.length === 0 ? (
							<Tooltip title='This facility has no associated product codes' placement='top' arrow>
								<Select required fullWidth disabled />
							</Tooltip>
						) : (
							<Autocomplete
								className='pca__manualInvoiceForm-productAndRateCodeAutocomplete'
								ListboxProps={{ className: 'pca__ratesForm-productAndRateCodeAutoComplete' }}
								options={productCodes.map((option: any) => option.name)}
								freeSolo
								size='medium'
								// disabled={row.editable === false}
								value={defaultProductCode?.name}
								renderOption={(props: any, option: any) => (
									<li value={props['data-option-index']} {...props} key={option.id}>
										{option}
									</li>
								)}
								renderInput={(params: any) => (
									<TextField
										{...params}
										// error={error}
										placeholder='Product Code'
										inputProps={{
											...params.inputProps,
											'data-cy': `product-code-autocomplete-default-rate`,
										}}
									/>
								)}
								onChange={(event, option: string | null) => {
									const selectedOption = productCodes.find((pc: any) => pc.name === option);
									setDefaultProductCode(selectedOption);
								}}
							/>
						)}
					</Grid>

					<Grid item xs={6}>
						<InputLabel>Rate Code</InputLabel>
						{rateCodes.length === 0 ? (
							<Tooltip title='This facility has no associated product codes' placement='top' arrow>
								<Select required fullWidth disabled />
							</Tooltip>
						) : (
							<Autocomplete
								className='pca__manualInvoiceForm-productAndRateCodeAutocomplete'
								ListboxProps={{ className: 'pca__ratesForm-productAndRateCodeAutoComplete' }}
								options={rateCodes.map((option: any) => option.name)}
								freeSolo
								size='medium'
								// disabled={row.editable === false}
								value={defaultRateCode?.name}
								renderOption={(props: any, option: any) => (
									<li value={props['data-option-index']} {...props} key={option.id}>
										{option}
									</li>
								)}
								renderInput={(params: any) => (
									<TextField
										{...params}
										// error={error}
										placeholder='Rate Code'
										inputProps={{
											...params.inputProps,
											'data-cy': `product-code-autocomplete-default-rate`,
										}}
									/>
								)}
								onChange={(event, option: string | null) => {
									const selectedOption = rateCodes.find((rc: any) => rc.name === option);
									setDefaultRateCode(selectedOption);
								}}
							/>
						)}
					</Grid>
				</>
			) : (
				<>
					{isChild && !selectedRate?.parentRateId && showFixedFeeForm ? (
						<Grid item xs={12}>
							<Grid container spacing={2}>
								<FixedFeeForm
									fixedFeeName={fixedFeeName}
									setFixedFeeName={setFixedFeeName}
									fixedFeeNetAmount={fixedFeeNetAmount}
									setFixedFeeNetAmount={setFixedFeeNetAmount}
								/>
							</Grid>
						</Grid>
					) : (
						<>
							<Grid item xs={6}>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<InputLabel>
											Monthly Fee
											<span className='pca__ratesForm-required'> *</span>
										</InputLabel>
										<FeeAutoComplete
											id='monthly-fee-select-menu'
											disabled={
												isDefault ||
												// Disable for parent rate when editing
												(selectedRate && !isChild) ||
												// Disable for children rate when editing an already submitted child
												(selectedRate && isChild && !!selectedRate?.parentRateId) ||
												// Disable for children rate not submitting when discount type !== 'FEE'
												(selectedRate && isChild && childDiscountType !== 'FEE')
											}
											selectedFee={monthlyFee}
											feeOptions={feeOptions.filter(({ feeType }) => feeType.name === 'Monthly')}
											handleChange={setMonthlyFee}
											error={missingFields.includes('monthlyFeeName')}
										/>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs={6}>
								<InputLabel>Product And Rate Code</InputLabel>
								<ProductRateCodeAutoComplete
									id='product-and-rate-code-select-menu'
									disabled={
										isDefault ||
										// Disable for parent rate when editing
										(selectedRate && !isChild) ||
										// Disable for children rate when editing an already submitted child
										(selectedRate && isChild && !!selectedRate?.parentRateId) ||
										// Disable for children rate not submitting when discount type !== 'FEE'
										(selectedRate && isChild && childDiscountType !== 'FEE')
									}
									selectedFee={monthlyFee}
									feeOptions={feeOptions.filter(({ feeType }) => feeType.name === 'Monthly')}
									handleChange={setMonthlyFee}
									error={missingFields.includes('monthlyFeeName')}
								/>
							</Grid>
						</>
					)}
					{childDiscountType === 'FEE' && isChild && !selectedRate?.parentRateId && (
						<Grid item xs={12}>
							<Grid
								container
								display='flex'
								direction='row'
								spacing={2}
								alignItems='center'
								justifyContent='space-between'
							>
								<FixedFeeFormToggle
									showFixedFeeForm={showFixedFeeForm}
									setShowFixedFeeForm={setShowFixedFeeForm}
								/>
								{showFixedFeeForm && (
									<Grid item xs={6}>
										<Typography variant='subtitle2' color='textSecondary'>
											Taxes will be inherited from the Parent Monthly Fee.
										</Typography>
									</Grid>
								)}
							</Grid>
						</Grid>
					)}
				</>
			)}
			<Grid className='pca__ratesForm-containerPadding' spacing={2} container>
				<Grid item xs={6}>
					<InverseButton
						fullWidth
						onClick={() => {
							setTabValue('2');
							setActiveStep(1);
							setActiveStep(isChild ? 2 : 1);
							setTabValue(isChild ? '3' : '2');
						}}
						data-cy='previous-button'
						name='Previous'
						theme={theme}
					/>
				</Grid>
				<Grid item xs={6}>
					<Button fullWidth onClick={handleNext} data-cy='next-step-button'>
						Next
					</Button>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default BillingAndRates;
